import React from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
  UIFormRow,
  UIFormLabels,
  UIFormFields,
  UIFormLabel
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIInput from '@/components/shared/UIInput';
import UISelect from '@/components/shared/UISelect';
import UIButton from '@/components/shared/UIButton';
import UISpinner from '@/components/shared/UISpinner';
import { useProfile } from '@/hooks/useProfile';
import { countryOptions, provinceOptions } from '@/data/options';
import './CompanyInformationSection.scss';

export interface CompanyInformationSectionProps {
  className?: string;
}

const baseClass = 'CompanyInformationSection';

const CompanyInformationSection: React.FC<CompanyInformationSectionProps> = ({ className = '' }) => {
  const {
    data,
    initialData,
    loading,
    handleChange,
    handleSave,
    handleClear,
  } = useProfile('company');

  const hasChanges =
    data.company_name !== initialData.company_name ||
    data.company_position !== initialData.company_position ||
    data.company_country !== initialData.company_country ||
    data.company_province !== initialData.company_province ||
    data.company_city !== initialData.company_city ||
    data.company_address !== initialData.company_address ||
    data.company_apt_suite !== initialData.company_apt_suite ||
    data.company_unit !== initialData.company_unit ||
    data.company_municipality !== initialData.company_municipality ||
    data.company_postal_code !== initialData.company_postal_code ||
    data.company_phone !== initialData.company_phone ||
    data.company_email !== initialData.company_email;

  if (loading) {
    return (
      <div className={`${baseClass}__loading`} style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <UISpinner />
      </div>
    );
  }

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      layout="form-horizontal"
      formPadding="large"
      labelWidth="120px"
      formGap="15px"
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Company information"
          description="Update some company information. Your address will never be publicly available."
        />
      </UISectionHeader>

      <UISectionBody>
        <UIFormRow>
          <UIFormLabels>
            <UIFormLabel>Company name</UIFormLabel>
            <UIFormLabel>Your position</UIFormLabel>
            <UIFormLabel>Country</UIFormLabel>
            <UIFormLabel>Province</UIFormLabel>
            <UIFormLabel>City</UIFormLabel>
            <UIFormLabel>Address</UIFormLabel>
            <UIFormLabel>Municipality</UIFormLabel>
            <UIFormLabel>Postal code</UIFormLabel>
            <UIFormLabel>Telephone No.</UIFormLabel>
            <UIFormLabel>Email address</UIFormLabel>
          </UIFormLabels>

          <UIFormFields>
            <UIInput
              placeholder="Company name"
              fullWidth
              value={data.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
            />
            <UIInput
              placeholder="Your position"
              fullWidth
              value={data.company_position || ''}
              onChange={(e) => handleChange('company_position', e.target.value)}
            />
            <UISelect
              options={countryOptions}
              placeholder="Select country"
              fullWidth
              value={data.company_country || ''}
              onChange={(e) => handleChange('company_country', e.target.value)}
            />
            <UISelect
              options={provinceOptions}
              placeholder="Select province"
              fullWidth
              value={data.company_province || ''}
              onChange={(e) => handleChange('company_province', e.target.value)}
            />
            <UIInput
              placeholder="City"
              fullWidth
              value={data.company_city || ''}
              onChange={(e) => handleChange('company_city', e.target.value)}
            />
            <div className={`${baseClass}__address-row`}>
              <UIInput
                placeholder="Address"
                className={`${baseClass}__address-row-small`}
                value={data.company_address || ''}
                onChange={(e) => handleChange('company_address', e.target.value)}
              />
              <UIInput
                placeholder="Apt, suite"
                className={`${baseClass}__address-row-large`}
                value={data.company_apt_suite || ''}
                onChange={(e) => handleChange('company_apt_suite', e.target.value)}
              />
              <UIInput
                placeholder="Unit"
                className={`${baseClass}__address-row-small`}
                value={data.company_unit || ''}
                onChange={(e) => handleChange('company_unit', e.target.value)}
              />
            </div>
            <UIInput
              placeholder="Municipality"
              fullWidth
              value={data.company_municipality || ''}
              onChange={(e) => handleChange('company_municipality', e.target.value)}
            />
            <UIInput
              placeholder="Postal code"
              fullWidth
              value={data.company_postal_code || ''}
              onChange={(e) => handleChange('company_postal_code', e.target.value)}
            />
            <UIInput
              placeholder="Telephone No."
              fullWidth
              value={data.company_phone || ''}
              onChange={(e) => handleChange('company_phone', e.target.value)}
            />
            <UIInput
              placeholder="Email address"
              fullWidth
              value={data.company_email || ''}
              onChange={(e) => handleChange('company_email', e.target.value)}
            />
          </UIFormFields>
        </UIFormRow>
      </UISectionBody>

      <UISectionFooter>
        <div className={`${baseClass}__buttons`}>
          <UIButton variant="primary" onClick={handleSave} disabled={!hasChanges}>
            Save
          </UIButton>
          <UIButton variant="primary" outline onClick={handleClear}>
            Clear
          </UIButton>
        </div>
      </UISectionFooter>
    </UISectionWrapper>
  );
};

export default CompanyInformationSection;